import { Theme } from '@mui/material/styles'

import motion from './motion'
import { FOCUS_VISIBLE, focusRingShadow } from './overrides/Button'
import { colorTokens } from './tokens.generated'

// ----------------------------------------------------------------------
// One answer to the pointer, exposed on the theme as `theme.hover`.
//
// Everything pressable answers, and everything of one kind answers the same
// way. Inconsistent feedback is worse than none: a console where some rows
// light up and some do not teaches an operator that the silent ones are
// disabled, and they go and look for the reason.
//
// Three kinds and three answers. A **surface** (a row, a nav item, a menu
// item, an icon button, a ghost button) takes the sunken surface under it. A
// **filled** thing keeps the hover its own variant states, because moving a
// fill to the sunken surface would throw away the ink it promised. A **text**
// thing underlines and steps its ink up.
//
// The value is `surfaceSunken`, which is the same value the token source
// already names for a row hover. It is written once here because the theme
// used to ship four of them (`background.neutral`, `action.hover`,
// `action.selected` and `grey[300]`) and no two rows hovered the same.
//
// A selected surface answers with its **ink**, not its fill: the fill is
// already saying that it is the selected one, so a second fill on top of it
// either reads as a different selection or, as it did here, as nothing at all.
//
// Whatever the kind, hover changes paint and never the box. No border where
// there was none, no padding, no shadow: a hover that resizes a control
// shifts every neighbour beside it, which is why the transition names the
// properties it is allowed to move rather than `all`.
// ----------------------------------------------------------------------

export type HoverMode = 'light' | 'dark'

const forMode = (mode: HoverMode) => {
  const colors = colorTokens[mode]
  return {
    /** What a pressable surface goes to under the pointer. */
    surface: colors.surfaceSunken,
    /** The next step down, for a control already on the sunken surface, and for a press. */
    sunken: colors.border,
    /** The ink a surface or a text answer steps up to. */
    ink: colors.text1,
    /** The ink a navigable text answer steps to. */
    linkInk: colors.accentDark,
    /** The ink a selected surface steps to, over the accent ground it already sits on. */
    selectedInk: colors.accentLightInk,
    /**
     * The edge a bordered control darkens to. A button or a field standing on
     * the sunken surface (a panel head, a toolbar) has a fill that is
     * already the fill the answer would move it to, so the edge is the half of
     * the answer that does not depend on what is behind it.
     */
    edge: colors.text3,
    /** Paint, and only paint, on the state duration. */
    transition: motion.state.transition(
      'background-color',
      'color',
      'border-color',
      'text-decoration-color',
    ),
  } as const
}

const hover = {
  light: forMode('light'),
  dark: forMode('dark'),
} as const

export type Hover = ReturnType<typeof forMode>

declare module '@mui/material/styles' {
  interface Theme {
    hover: Hover
  }
  interface ThemeOptions {
    hover?: Hover
  }
}

/**
 * The surface answer, whole: the fill, the ink it lifts to, the press, and the
 * keyboard twin. A hover without a `:focus-visible` beside it is an affordance
 * only a mouse can see.
 *
 * Anything with a selected state excludes it with `:not(.Mui-selected)` and
 * states its own selected answer, rather than out-specifying this one. Two
 * rules of equal specificity are decided by which was written last, silently.
 */
export const surfaceHover = (theme: Theme) => ({
  'transition': theme.hover.transition,
  '&:hover': {
    backgroundColor: theme.hover.surface,
    color: theme.hover.ink,
  },
  '&:active': {
    backgroundColor: theme.hover.sunken,
  },
  [FOCUS_VISIBLE]: {
    outline: 'none',
    boxShadow: focusRingShadow(theme),
  },
})

/**
 * The text answer: an underline and a step of ink. The underline is what makes
 * it read as navigable rather than as a label that happened to change colour,
 * and it costs the box nothing.
 */
export const textHover = (theme: Theme, ink: string) => ({
  'transition': theme.hover.transition,
  '&:hover': {
    color: ink,
    textDecoration: 'underline',
  },
  [FOCUS_VISIBLE]: {
    outline: 'none',
    boxShadow: focusRingShadow(theme),
  },
})

/**
 * What a control in a strip or a head shows the keyboard: the answer the
 * pointer gets, its underline drawn heavier, and no ring. A tab and a sortable
 * column head take it whole; a segmented cell takes the same underline over
 * its own surface answer (`overrides/ToggleButton`). A toggle on its own keeps
 * the ring: it has an edge, and often only a glyph to underline.
 *
 * The ring is the right answer for a control with an edge of its own, and the
 * wrong one for these. A tab strip scrolls, and its scroller cut a focused
 * tab's ring to two bars either side of the word; a column head's ring drew a
 * box round one word in a row of words; a segmented cell's ringed the thumb
 * that is already the selection. Each read as a stray border on a control
 * already showing its state, and were asked to go on 2026-11-01. An underline
 * is a change of shape as well as of ink, so the keyboard still gets an
 * answer it can see, and the same one a mouse gets.
 */
export const STRIP_FOCUS = {
  outline: 'none',
  boxShadow: 'none',
  textDecoration: 'underline',
  // Heavier than the pointer's, so a tab under a resting mouse and the one
  // the keyboard is on never look alike.
  textDecorationThickness: '2px',
} as const

/** `textHover` for a control in a strip or a head: see `STRIP_FOCUS`. */
export const stripTextHover = (theme: Theme, ink: string) => ({
  'transition': theme.hover.transition,
  '&:hover': {
    color: ink,
    textDecoration: 'underline',
  },
  [FOCUS_VISIBLE]: {
    ...STRIP_FOCUS,
    color: ink,
  },
})

export default hover
